import React, { AnchorHTMLAttributes } from 'react';

export type ExternalLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  rel?: string;
  href: string;
  noReferrer?: boolean;
  anchorElement?: React.ReactNode;
};

/**
 * Renders a '<a />' that opens up a new tab and is meant to be used with an
 * external page.
 *
 * @example
 * ```javascript
 * // Renders:
 * // <a href="www.google.com" target="_blank" rel="noopener noreferrer">
 * //   link test
 * // </a>
 * //
 * const extLink = <ExternalLink href="www.google.com">link text</ExternalLink>;
 * ```
 */
function ExternalLink({
  rel: relFromProps,
  href,
  target = '_blank',
  noReferrer = true,
  anchorElement,
  ...otherProps
}: ExternalLinkProps) {
  const rel = React.useMemo(() => {
    let output: string;

    if (!relFromProps) {
      output = 'noopener';
    } else if (!relFromProps.includes('noopener')) {
      output = relFromProps + ' noopener';
    } else {
      output = relFromProps;
    }

    if (noReferrer) {
      output += ' noreferrer';
    }
    return output;
  }, [noReferrer, relFromProps]);

  const computedProps = {
    target,
    rel,
    href,
    ...otherProps,
  };

  if (React.isValidElement(anchorElement)) {
    return React.cloneElement(anchorElement, computedProps);
  }

  return <a {...computedProps} />;
}

export default ExternalLink;
